<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2CloudReq } from 'src/services/gen_2_http_reqs';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Input, Select, Stack, Text, Toggle, toast } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { metersToFeetInches } from 'src/helpers/units/elevation_units';
	import {
		validateZoneDetectionHeights,
		formatZoneDetectionHeightErrors,
		detectionHeightToastTitle,
		DETECTION_HEIGHT_MAX_M,
		maxDetectionHeightInputMinBound,
		minDetectionHeightInputMaxBound,
		type ZoneDetectionHeightValidation,
	} from 'src/components/Radar/helpers/radar-helper';

	export let device: Device;

	/** Localizes issues from `validateZoneDetectionHeights` using this component’s `format` store. */
	function formatDetectionHeightErrors(validation: ZoneDetectionHeightValidation) {
		return formatZoneDetectionHeightErrors(validation, get(format));
	}

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;

	let waitForResponse = false;
	let tiltCalibrating = false;

	$: sensorTiltDisplay =
		settings.presence?.sensor?.tilt != null &&
		settings.presence?.sensor?.tilt !== '' &&
		!Number.isNaN(Number(settings.presence.sensor.tilt))
			? `${Number(settings.presence.sensor.tilt).toFixed(2)}°`
			: '';

	$: transmissionPowerOptions = [
		{ title: $format('presence_config_power_low', { default: 'Low' }), value: 'low' },
		{ title: $format('presence_config_power_medium', { default: 'Medium' }), value: 'medium' },
		{ title: $format('presence_config_power_high', { default: 'High' }), value: 'high' },
	];

	$: sensitivityOptions = [
		{ title: $format('presence_config_power_low', { default: 'Low' }), value: 'low' },
		{ title: $format('presence_config_power_medium', { default: 'Medium' }), value: 'medium' },
		{ title: $format('presence_config_power_high', { default: 'High' }), value: 'high' },
	];

	const getSelectOption = (e: CustomEvent | TouchEvent): string => {
		if (typeof e.detail === 'string') return e.detail;
		if (e.detail?.value) return e.detail.value as string;
		return 'medium';
	};

	$: selectedPowerOption = transmissionPowerOptions.find(
		(opt) => opt.value === (power ?? settings.presence?.sensor?.power),
	);

	$: selectedSensitivityOption = sensitivityOptions.find(
		(opt) => opt.value === (sensitivity ?? settings.presence?.sensor?.sensitivity),
	);

	let enable = settings.presence?.enable ?? true;
	let isFlipped = settings.presence?.sensor?.flipped ?? false;
	let imperial = settings.presence?.ui?.imperial ?? false;
	let configMinHeight = settings.presence?.zmin ?? 0;
	let configMaxHeight = settings.presence?.zmax ?? 3;
	let configSensorHeight = settings.presence?.sensor?.height ?? 2;
	let power = settings.presence?.sensor?.power ?? 'high';
	let sensitivity = settings.presence?.sensor?.sensitivity ?? 'medium';

	$: parsedSettingsMax = Number(configMaxHeight);
	$: parsedSettingsMin = Number(configMinHeight);
	$: settingsMinHeightInputMax = minDetectionHeightInputMaxBound(parsedSettingsMax);
	$: settingsMaxHeightInputMin = maxDetectionHeightInputMinBound(parsedSettingsMin);

	function formatHeightDualMetersFeet(value: string | number): string {
		const m = typeof value === 'number' ? value : parseFloat(String(value));
		const safeM = Number.isFinite(m) ? m : 0;
		const metersStr = `${Number(safeM.toFixed(2))} m`;
		const { ft, inch } = metersToFeetInches(safeM);
		return `( ${metersStr} / ${ft} ft ${inch} in )`;
	}

	$: minHeightLabelPlain = $format('presence_config_min_height_label', {
		default: 'Min detection height',
	});
	$: maxHeightLabelPlain = $format('presence_config_max_height_label', {
		default: 'Max detection height',
	});
	$: sensorHeightLabelPlain = $format('presence_config_sensor_height_label', {
		default: 'Sensor height',
	});

	$: minHeightFieldLabel = imperial
		? `${minHeightLabelPlain} ${formatHeightDualMetersFeet(configMinHeight)}`
		: minHeightLabelPlain;
	$: maxHeightFieldLabel = imperial
		? `${maxHeightLabelPlain} ${formatHeightDualMetersFeet(configMaxHeight)}`
		: maxHeightLabelPlain;
	$: sensorHeightFieldLabel = imperial
		? `${sensorHeightLabelPlain} ${formatHeightDualMetersFeet(configSensorHeight)}`
		: sensorHeightLabelPlain;

	const save = async () => {
		const raw = validateZoneDetectionHeights(String(configMinHeight), String(configMaxHeight));
		const { minError, maxError } = formatDetectionHeightErrors(raw);
		if (minError || maxError) {
			const _format = get(format);
			toast.open({
				type: 'danger',
				title:
					detectionHeightToastTitle(minError, maxError) ||
					_format('presence_height_error_invalid_generic', {
						default: 'Check min and max detection height.',
					}),
			});
			return;
		}

		waitForResponse = true;
		const res = await gen2Request(
			device,
			'Presence.SetConfig',
			{
				config: {
					enable,
					zmin: Number(configMinHeight),
					zmax: Number(configMaxHeight),
					sensor: {
						height: Number(configSensorHeight),
						flipped: isFlipped,
						power,
						sensitivity,
					},
					ui: {
						imperial,
					},
				},
			},
			true,
		);
		onSettingsResponse(res, device);
		if (res.isok) {
			if (!settings.presence) settings.presence = {};
			settings.presence.enable = enable;
			settings.presence.zmin = Number(configMinHeight);
			settings.presence.zmax = Number(configMaxHeight);
			if (!settings.presence.sensor) settings.presence.sensor = {};
			settings.presence.sensor.height = Number(configSensorHeight);
			settings.presence.sensor.flipped = isFlipped;
			settings.presence.sensor.power = power;
			settings.presence.sensor.sensitivity = sensitivity;
			if (!settings.presence.ui) settings.presence.ui = {};
			settings.presence.ui.imperial = imperial;
			devices_settings.update_devices_settings(id, settings);
		}
		waitForResponse = false;
	};

	async function handleTiltCalibrate() {
		if (!device || tiltCalibrating) return;
		tiltCalibrating = true;
		try {
			const result = await gen2Request(device, 'Presence.TiltCalibrate', {}, true);
			if (result?.isok) {
				toast.open({
					type: 'success',
					title: $format('presence_toast_tilt_calibrate_success', {
						default: 'Tilt calibrated successfully.',
					}),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('presence_toast_tilt_calibrate_error', {
						default: 'Tilt calibration failed.',
					}),
				});
			}
		} finally {
			tiltCalibrating = false;
		}
	}
</script>

<Stack gap="s" class="mt-s">
	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_enable_detection_label', { default: 'Enable detection' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_enable_detection_tooltip', {
			default: 'Activate presence detection',
		})}
	</Text>
	<Toggle
		aria-label={$format('toggle_enable_detection', { default: 'Enable detection' })}
		bind:checked={enable}
		size="m"
		id="toggle-enable-detection-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_flipped_label', { default: 'Flipped mounting' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_flipped_tooltip', {
			default: 'Enable if the device is mounted upside down (rotated 180°)',
		})}
	</Text>
	<Toggle
		aria-label={$format('toggle_flipped_mounting', { default: 'Flipped mounting' })}
		bind:checked={isFlipped}
		size="m"
		id="toggle-flipped-mounting-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_imperial_label', { default: 'Enable imperial units' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_imperial_tooltip', {
			default: 'Display metric and imperial units',
		})}
	</Text>
	<Toggle
		aria-label={$format('toggle_imperial_units', { default: 'Enable imperial units' })}
		bind:checked={imperial}
		size="m"
		id="toggle-imperial-units-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{minHeightFieldLabel}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_min_height_tooltip', {
			default:
				'Set the minimum height above the floor at which presence can be detected. Allowed range: 0 m up to (max detection height − 0.1 m).',
		})}
	</Text>
	<Input
		type="number"
		min="0"
		max={settingsMinHeightInputMax}
		step="0.01"
		class="text-left"
		bind:value={configMinHeight}
		label={minHeightFieldLabel}
		id="input-min-height-settings"
		aria-labelledby="input-min-height-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{maxHeightFieldLabel}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_max_height_tooltip', {
			default:
				'Set the maximum height above the floor at which presence can be detected. Allowed range: ({minHeight+0.1}m +) up to 5 m.',
		})}
	</Text>
	<Input
		type="number"
		min={settingsMaxHeightInputMin}
		max={DETECTION_HEIGHT_MAX_M}
		step="0.01"
		bind:value={configMaxHeight}
		class="text-left"
		label={maxHeightFieldLabel}
		id="input-max-height-settings"
		aria-labelledby="input-max-height-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{sensorHeightFieldLabel}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_sensor_height_tooltip', {
			default: 'Set the height of the sensor relative to the floor. Range: 0 to 5 m.',
		})}
	</Text>
	<Input
		type="number"
		min="0"
		max={DETECTION_HEIGHT_MAX_M}
		step="0.01"
		bind:value={configSensorHeight}
		label={sensorHeightFieldLabel}
		id="input-sensor-height-settings"
		aria-labelledby="input-sensor-height-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_sensor_tilt_label', { default: 'Sensor tilt' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_sensor_tilt_tooltip', {
			default:
				'Current tilt angle reported by the sensor. Use Calibrate to reset tilt measurement after mounting.',
		})}
	</Text>
	<div class="tilt-field">
		<div class="tilt-field__input">
			<Input
				type="text"
				readonly
				disabled
				value={sensorTiltDisplay}
				label={$format('presence_config_sensor_tilt_label', { default: 'Sensor tilt' })}
				id="input-sensor-tilt-settings"
				aria-labelledby="input-sensor-tilt-settings"
			/>
		</div>
		<div class="tilt-field__action mt-m">
			<Button
				variant="secondary"
				size="m"
				disabled={tiltCalibrating || !device}
				on:click={handleTiltCalibrate}
			>
				{$format('presence_config_sensor_tilt_calibrate', { default: 'Calibrate' })}
			</Button>
		</div>
	</div>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_power_label', { default: 'Transmission power' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_power_tooltip', {
			default: 'Set the transmission power of the radar sensor.',
		})}
	</Text>
	<Select
		aria-labelledby="select-power-settings"
		icon={null}
		options={transmissionPowerOptions}
		label={$format('presence_config_power_label', { default: 'Transmission power' })}
		value={selectedPowerOption}
		on:change={(e) => (power = getSelectOption(e))}
		id="select-power-settings"
	/>

	<Text styleType="body" class="mb-xs mt-s">
		{$format('presence_config_sensitivity_label', { default: 'Detection sensitivity' })}
	</Text>
	<Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_config_sensitivity_tooltip', {
			default:
				'Set the detection sensitivity level. High sensitivity improves detection of stationary objects; low sensitivity reduces false triggers.',
		})}
	</Text>
	<Select
		aria-labelledby="select-sensitivity-settings"
		icon={null}
		options={sensitivityOptions}
		label={$format('presence_config_sensitivity_label', {
			default: 'Detection sensitivity',
		})}
		value={selectedSensitivityOption}
		on:change={(e) => (sensitivity = getSelectOption(e))}
		id="select-sensitivity-settings"
	/>

	{#if !$showNewLayout}
		<hr class="mt-m mb-s" />
		<Button loading={waitForResponse} size="m" width="full" on:click={save} class="mb-s">
			{$format('button_save', { default: 'Save' })}
		</Button>
	{:else}
		<div class="presence-config-footer">
			<Button loading={waitForResponse} size="m" width="full" on:click={save}>
				{$format('button_save', { default: 'Save' })}
			</Button>
		</div>
	{/if}
</Stack>

<style>
	.presence-config-footer {
		position: sticky;
		bottom: 0;
		padding: 12px 0;
		background: var(--color-surface, var(--color-base));
		border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		z-index: 1;
	}
</style>
